import store from '../store';
import router from '../router';
import { setApiLanguageIdResolver } from '../utils/apiLanguageId.js';
import { startAttributionCaptureLifecycle, runAttributionCapture } from '../attribution/capture.js';
import { mountVueIslands } from './mountIslands';
import { registerWindowApi, installLegacyDomEnhancements } from './legacyPageBindings';
import { scheduleIdleWork } from '../utils/scheduleIdleWork.js';
import { prefetchBuyVignetteDocument, prefetchBuyVignetteChunk } from '../utils/prefetchBuyVignette.js';
import { applyAxiosCsrfToken } from '../bootstrap.js';
import {
    initBlogArticleToc,
    initBlogImageLightbox,
    initFaqDetailsGroups,
    initHeaderNavDropdowns,
    registerShowCountryMethods,
} from './pageScripts.js';
import { initValidityCheckForm } from './validityCheckForm.js';

function readBootstrapFromWindow() {
    return typeof window !== 'undefined' ? window.__APP_DATA__ : null;
}

function rememberSsrPlate() {
    try {
        const typed = document.querySelector('[data-home-license-plate-search-ssr="1"] input');
        if (typed && typed.value) {
            window.__CV_SSR_PLATE = typed.value;
        }
    } catch (e) {}
}

/**
 * Previously lived on the compiled #app root. Language changes still refetch
 * countries and the product catalog for every page that boots the store.
 */
function watchLanguageChanges() {
    store.watch(
        (state) => state.lang?.id,
        (newId, oldId) => {
            if (oldId == null || newId == null || newId === oldId) return;
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('langId', String(newId));
            }
            store.dispatch('form/alignPlateCountryWithSiteLanguage').catch(() => {});
            store.dispatch('form/reloadCountries').catch(() => {});
            store.dispatch('form/reloadProductsCatalog').catch(() => {});
        }
    );
}

function startPageScripts() {
    rememberSsrPlate();
    watchLanguageChanges();
    initValidityCheckForm();
    scheduleIdleWork(() => {
        initHeaderNavDropdowns();
        initBlogArticleToc();
        initBlogImageLightbox();
        initFaqDetailsGroups();
        registerShowCountryMethods();
    });
}

function prefetchBuyVignetteFromCart() {
    if (typeof document === 'undefined' || !document.querySelector('shopping-cart')) return;
    const href = document.querySelector('[data-shopping-cart-ssr="1"] a[href]')?.getAttribute('href');
    prefetchBuyVignetteDocument(href);
    prefetchBuyVignetteChunk();
}

/**
 * Boots Vuex, router hooks, and the islands present in the Blade document.
 * #app is left as server-rendered HTML. The buy-vignette date picker loads
 * Vuetify on open, so that chunk does not block the checkout form.
 */
export function runApplication() {
    try {
        if (typeof window !== 'undefined' && window.__CV_APP_BOOTED__) {
            return;
        }
        if (typeof window !== 'undefined') {
            window.__CV_APP_BOOTED__ = true;
        }

        applyAxiosCsrfToken();

        const bootstrapAppData = readBootstrapFromWindow();

        registerWindowApi(store);
        store.dispatch('bootstrapFromWindow');

        if (bootstrapAppData && !bootstrapAppData.euroOnlyCurrency && !bootstrapAppData.secondHomeFunnel) {
            store.dispatch('form/reloadProductsCatalog').catch(() => {});
        }

        setApiLanguageIdResolver(() => {
            const id = store.state.lang?.id;
            if (id !== undefined && id !== null && id !== '') return id;
            if (typeof localStorage !== 'undefined') {
                return localStorage.getItem('langId');
            }
            return null;
        });

        scheduleIdleWork(() => {
            store.dispatch('loadLanguages').catch(() => {});
        });

        scheduleIdleWork(() => {
            startAttributionCaptureLifecycle();
            router.afterEach(() => {
                runAttributionCapture();
            });
        });

        scheduleIdleWork(() => {
            installLegacyDomEnhancements();
        });

        startPageScripts();
        mountVueIslands({ mountWithoutVuetify: false });
        scheduleIdleWork(prefetchBuyVignetteFromCart, { timeout: 1500 });
    } catch (err) {
        if (typeof console !== 'undefined' && console.error) {
            console.error('[app] runApplication failed', err);
        }
    }
}
